<template>
  <div>
    <div class="form__content">
      <h1 class="extra-large-headline">Credit/Debit Card Verification</h1>
      <p class="bodycopy" id="bodyCopy">
        Please provide a selfie of you holding the card used for payment and a
        note stating $entity and today's date.
      </p>
      <ul class="form__selfie-instructions bodycopy">
        <li>Your face is clearly visible</li>
        <li>Name and expiration date are visible</li>
        <li>First 4 and last 4 digits are visible</li>
        <li>Middle 8 digits must be covered (Example: 1337 **** **** 1337)</li>

        <li>
          Ensure that we can clearly see your face in the selfie, no hats or
          sunglasses please!
        </li>
        <li>Ensure that your document is legible.</li>
        <li>The photo needs to have been taken today.</li>
      </ul>
    </div>

    <div class="selfie-content-container">
      <img class="selfie-image" src="@/assets/images/identity/ccSelfie.png" />
      <div class="selfie-content left">
        <p>{{ form.selfieImgContent.entity }}</p>
        <p>{{ form.selfieImgContent.date }}</p>
      </div>
      <div class="selfie-content right">
        <p>{{ form.selfieImgContent.entity }}</p>
        <p>{{ form.selfieImgContent.date }}</p>
      </div>
    </div>

    <form @submit.prevent class="form__content">
      <FileUploadField
        name="selfieDocument"
        label="Upload your selfie"
        placeholder="Select or drag your file"
        iconType="VerificationCircle"
        :errors="form.selfieDocumentErrors"
        @updateFile="updateFiles"
      />

      <ButtonBase
        :isDisabled="formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        My selfie is clear and legible
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useApiHelpers from "@/composables/api/helpers";
import useCustomerActions from "@/composables/api/useCustomerActions";

import FileUploadField from "@/components/ui/inputs/FileUploadField.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import useFormValidation from "@/composables/validations/useFormValidation";

const router = useRouter();
const globalStore = useGlobalStore();
const identityQueueStore = useIdentityQueueStore();

const { validateFileInput } = useFormValidation();
const { postRequest } = useApiHelpers();
const { fetchCustomerId } = useCustomerActions();
const emit = defineEmits(["onPrimaryButtonClick", "onSecondaryButtonClick"]);
const props = defineProps({
  mode: {
    type: String,
    default: "form",
  },
});
const form = ref({
  docType: "others",
  selfieDocument: [],
  selfieDocumentErrors: "",
  selfieImgContent: {
    entity: "",
    date: null,
  },
});

const updateFiles = (file) => {
  const fileValidationErrors = validateFileInput("selfieDocument", file);
  form.value.selfieDocument = file;

  form.value.selfieDocumentErrors = fileValidationErrors;
};

const handleFormSubmit = async () => {
  const custId = await fetchCustomerId();
  const formData = new FormData();
  const docSubType = "cc_selfie";

  formData.set("documentFile", form.value.selfieDocument);
  formData.set("documentType", form.value.docType);
  formData.set("documentSubType", docSubType);
  formData.set("documentReference", custId);

  globalStore.setIsFormLoading(true);
  postRequest(
    "documents",
    formData,
    () => {
      if (props.mode === "modal") emit("onPrimaryButtonClick");
      else router.push(identityQueueStore.getNextIdentityRoute);
    },
    () => {
      if (props.mode === "modal") emit("onPrimaryButtonClick"); // to close the modal on error
    }
  );
};

const formHasErrors = computed(() => {
  const anyErrors = form.value.selfieDocumentErrors !== "";
  const anyFieldsEmpty = form.value.selfieDocument.length === 0;
  return anyErrors || anyFieldsEmpty;
});
</script>

<style lang="scss" scoped>
.form__content {
  padding-bottom: 3rem;
  max-width: 54rem;
  padding: 0 2rem;
  :nth-child(1) {
    padding-top: 1rem;
  }
  @include breakpoint($min-m) {
    padding-inline: 0;
  }

  .bodycopy {
    margin-bottom: 1rem;
    @include breakpoint($min-m) {
      margin-bottom: 1.5rem;
    }
  }
  .form__selfie-instructions {
    list-style: unset;
    padding-inline: 1.5rem;

    li {
      font-size: $font-body;
      line-height: 1.5;
    }
  }
}

.selfie-image {
  display: flex;
  width: 100%;
  height: 503px;
  position: relative;

  @include breakpoint($max-sm) {
    height: 250px;
    width: 280px;
    margin: 0 auto;
  }
}
.selfie-content-container {
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: end;
  width: 100%;
  font-size: 10px;
  margin-bottom: 5px;
  color: $black;

  @include breakpoint($max-sm) {
    font-size: 7px;
    min-height: unset;
  }

  .selfie-content {
    width: 50%;
    text-align: center;
    position: absolute;

    p {
      margin: 0;
      max-width: 80px;
      color: $black;
      @include breakpoint($max-sm) {
        font-size: 8px;
      }
    }
    &.left {
      left: 69px;

      @include breakpoint($max-sm) {
        left: 55px;
      }
    }
    &.right {
      right: -49px;
      @include breakpoint($max-sm) {
        right: -3px;
      }
    }
  }
}
</style>
